'use client';
import React, { ReactNode, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { isBreakPointMobile } from '../components/video-player/utils/media-utils';

interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  portal?: boolean;
}

export const Tooltip = ({ content, children, position = 'top', portal = false }: TooltipProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  const showTooltip = () => {
    setIsVisible(true);
    if (portal && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const offset = 8;
      let top = rect.top;
      let left = rect.left;
      switch (position) {
        case 'top':
          top = rect.top - offset;
          left = rect.left + rect.width / 2;
          break;
        case 'bottom':
          top = rect.bottom + offset;
          left = rect.left + rect.width / 2;
          break;
        case 'left':
          top = rect.top + rect.height / 2;
          left = rect.left - offset;
          break;
        case 'right':
          top = rect.top + rect.height / 2;
          left = rect.right + offset;
          break;
      }
      setCoords({ top, left });
    }
  };

  const hideTooltip = () => {
    setIsVisible(false);
  };

  const tooltipPositionClasses = {
    top: 'bottom-full left-1/2 transform -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 transform -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 transform -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 transform -translate-y-1/2 ml-2',
  };

  const defaultTooltip = (
    <div
      className={`absolute z-10 px-2 py-1 text-sm text-white bg-black rounded opacity-75 whitespace-nowrap ${tooltipPositionClasses[position]}`}
    >
      {content}
    </div>
  );

  const portalTooltipStyles: React.CSSProperties = {
    position: 'fixed',
    top: coords.top,
    left: coords.left,
    transform:
      position === 'top'
        ? 'translate(-50%, -100%)'
        : position === 'bottom'
          ? 'translate(-50%, 0)'
          : position === 'left'
            ? 'translate(-100%, -50%)'
            : 'translate(0, -50%)',
    zIndex: 9999,
    padding: '0.25rem 0.5rem',
    fontSize: '0.875rem',
    color: 'white',
    backgroundColor: 'black',
    borderRadius: '0.25rem',
    opacity: 0.75,
    whiteSpace: 'nowrap',
  };

  const tooltipElement =
    isVisible && portal ? (
      <div style={portalTooltipStyles}>{content}</div>
    ) : isVisible ? (
      defaultTooltip
    ) : null;

  if (isBreakPointMobile()) return <>{children}</>;

  return (
    <div
      ref={containerRef}
      className="relative inline-block"
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
    >
      {children}
      {portal ? (isVisible ? createPortal(tooltipElement, document.body) : null) : tooltipElement}
    </div>
  );
};
